import React, { useCallback, useEffect, useState } from 'react';
import { pathsAPI } from '../api/pathsAPI';
import { displayText } from '../utils/displayText';
import { formatPlanDate } from '../utils/planDates';
import './MyPathFolder.css';

// The My Path folder in the chat sidebar, which is the path library
// (decision 5). It lists paths from GET /api/paths, not chat sessions, plus
// open creation sessions that have no path yet ("Planning…"), so a planning
// chat with no draft is still reachable. "New path" is on the header row;
// Completed and Archived are collapsed groups.
//
// Clicking a path opens it plan-only; a draft opens its planning
// conversation. The folder is rendered with the sidebar's own project
// classes, so it looks like the folder it replaces.

function sortNewestFirst(list) {
  return [...list].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));
}

export const PLANNING_POLL_MS = 10000;

function PathRow({ label, marker, active, muted, busy, onClick, title }) {
  return (
    <div className={`chat-agent-sidebar-item-wrap glass-pill my-path-row${active ? ' active' : ''}${muted ? ' my-path-row-muted' : ''}`}>
      <button
        type="button"
        className="chat-agent-sidebar-item"
        onClick={onClick}
        aria-current={active ? 'page' : undefined}
        title={title || label}
      >
        {busy && <span className="my-path-spinner" aria-hidden="true" />}
        <span className="chat-agent-sidebar-item-title">{label}</span>
        {marker && <span className="my-path-marker">{marker}</span>}
      </button>
    </div>
  );
}

function applyFolderLists(pathsResult, planningResult, setPaths, setPlanning, setState) {
  if (pathsResult.status === 'fulfilled' && Array.isArray(pathsResult.value)) {
    setPaths(pathsResult.value.filter((p) => p && p.id));
    setState('ready');
  } else {
    setState('error');
  }
  // The planning list is an extra: if it fails, the paths still show.
  setPlanning(planningResult.status === 'fulfilled' && Array.isArray(planningResult.value)
    ? planningResult.value.filter((s) => s && s.id)
    : []);
}

export default function MyPathFolder({
  name = 'My Path',
  controller,
  refreshKey = 0,
  filterText = '',
  forceOpen = false,
  onNavigate,
}) {
  const [expanded, setExpanded] = useState(true);
  const [paths, setPaths] = useState([]);
  const [planning, setPlanning] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (forceOpen) setExpanded(true);
  }, [forceOpen]);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([pathsAPI.listPaths(), pathsAPI.listOpenCreationSessions()]).then(([pathsResult, planningResult]) => {
      if (cancelled) return;
      applyFolderLists(pathsResult, planningResult, setPaths, setPlanning, setState);
    });
    return () => { cancelled = true; };
  }, [refreshKey, retryKey]);

  const drafting = planning.some((s) => s.generation_status === 'in_progress');
  useEffect(() => {
    if (!drafting) return undefined;
    let cancelled = false;
    const timer = setInterval(() => {
      Promise.allSettled([pathsAPI.listPaths(), pathsAPI.listOpenCreationSessions()]).then(([pathsResult, planningResult]) => {
        if (cancelled) return;
        applyFolderLists(pathsResult, planningResult, setPaths, setPlanning, setState);
      });
    }, PLANNING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [drafting]);

  const go = useCallback((fn) => {
    onNavigate?.();
    fn();
  }, [onNavigate]);

  const query = filterText.trim().toLowerCase();
  const matches = (text) => !query || text.toLowerCase().includes(query);

  const openPath = (p) => go(() => {
    if (p.status === 'draft' && p.latest_session_id) controller.openPlanning(p.latest_session_id);
    else controller.openPath(p.id);
  });

  const titleOf = (p) => displayText(p.title) || 'Untitled path';
  const isActive = (p) => controller.pathId === p.id
    || (p.status === 'draft' && Boolean(p.latest_session_id) && controller.planSessionId === p.latest_session_id);

  const planningRows = sortNewestFirst(planning).filter(() => matches('Planning…'));
  const current = paths.filter((p) => (p.status === 'active' || p.status === 'draft') && matches(titleOf(p)));
  const completed = paths.filter((p) => p.status === 'completed' && matches(titleOf(p)));
  const archived = paths.filter((p) => p.status === 'archived' && matches(titleOf(p)));
  const nothing = state === 'ready' && paths.length === 0 && planning.length === 0;

  return (
    <div className="chat-agent-sidebar-project chat-agent-sidebar-project-paths my-path-folder">
      <div className="chat-agent-sidebar-project-header glass-pill my-path-folder-head">
        <button
          type="button"
          className="chat-agent-sidebar-project-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${name}`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={expanded ? 'expanded' : ''}>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chat-agent-sidebar-project-paths-icon" aria-hidden="true">
          <path d="M3 12l6-6 4 4 8-8" />
          <path d="M17 2h4v4" />
        </svg>
        <button type="button" className="chat-agent-sidebar-project-name" onClick={() => setExpanded((v) => !v)}>
          {name}
        </button>
        <button
          type="button"
          className="chat-agent-sidebar-section-action my-path-new"
          onClick={() => go(controller.startNewPlan)}
          disabled={controller.creating}
        >
          + New path
        </button>
      </div>

      {expanded && (
        <div className="my-path-folder-body">
          {state === 'loading' && <p className="my-path-note">Loading your paths…</p>}
          {state === 'error' && (
            <p className="my-path-note">
              Your paths could not be loaded.{' '}
              <button type="button" className="my-path-link" onClick={() => setRetryKey((k) => k + 1)}>Try again</button>
            </p>
          )}
          {nothing && <p className="my-path-note">No paths yet. Start one with New path.</p>}

          {planningRows.map((s) => {
            const rowDrafting = s.generation_status === 'in_progress';
            const date = formatPlanDate(String(s.updated_at || s.created_at || '').slice(0, 10));
            return (
              <PathRow
                key={`planning-${s.id}`}
                label={rowDrafting ? 'Drafting…' : (date ? `Planning… · ${date}` : 'Planning…')}
                title={rowDrafting ? 'A plan being drafted' : "A plan you started but haven't drafted yet"}
                busy={rowDrafting}
                active={controller.planSessionId === s.id && !controller.pathId}
                onClick={() => go(() => controller.openPlanning(s.id))}
              />
            );
          })}

          {current.map((p) => {
            const ready = p.status === 'draft' && p.has_proposed_version;
            const updating = p.latest_attempt_status === 'in_progress' && (ready || p.status === 'active');
            const didntFinish = p.latest_attempt_status === 'failed' && !p.has_proposed_version && p.status === 'draft';
            let marker = null;
            if (updating) marker = 'Updating…';
            else if (didntFinish) marker = "Didn't finish";
            else if (ready) marker = 'Draft ready';
            return (
              <PathRow
                key={p.id}
                label={p.status === 'draft' && !ready ? `${titleOf(p)} (draft)` : titleOf(p)}
                marker={marker}
                busy={updating}
                title={titleOf(p)}
                active={isActive(p)}
                onClick={() => openPath(p)}
              />
            );
          })}

          {completed.length > 0 && (
            <details className="my-path-group">
              <summary>Completed · {completed.length}</summary>
              {completed.map((p) => (
                <PathRow key={p.id} label={titleOf(p)} muted active={isActive(p)} onClick={() => openPath(p)} />
              ))}
            </details>
          )}

          {archived.length > 0 && (
            <details className="my-path-group">
              <summary>Archived · {archived.length}</summary>
              {archived.map((p) => (
                <PathRow key={p.id} label={titleOf(p)} muted active={isActive(p)} onClick={() => openPath(p)} />
              ))}
            </details>
          )}
        </div>
      )}
    </div>
  );
}
